'use client';

/**
 * Best-effort revocation of ERC-7715 spend-permission grants from the
 * active wallet. Sibling of `use-grant-protection-permissions.ts`.
 *
 * Honest caveat — MetaMask support is unproven
 * ───────────────────────────────────────────
 * ERC-7715 defines `wallet_revokeExecutionPermissions({ permissionContext })`,
 * but no shipped MetaMask build / installed `@metamask/*` package in
 * this repo exposes it as a documented action, and `wallet_revokePermissions`
 * (MIP-2) targets permission *names*, not spend-permission contexts —
 * the wrong tool. So this hook is strictly best-effort and capability-
 * probed: if the wallet routes the method, stale grants get cleaned up;
 * if not (`-32601` / `4200`), we report `unsupported` and the caller
 * falls back to guiding the user to MetaMask's own permission manager.
 * It NEVER throws on an individual grant — a revoke failure must not
 * break the enable flow it's bolted onto.
 */

import { useCallback, useState } from 'react';
import { useWallets } from '@privy-io/react-auth';
import type { Hex } from 'viem';
import { useWallet } from './use-wallet';
import { isProviderError } from '@/lib/protection/grant-permissions';

/** Canonical ERC-7715 revoke method (post-rename, plural — mirrors the
 *  `wallet_requestExecutionPermissions` grant method). */
export const WALLET_REVOKE_METHOD = 'wallet_revokeExecutionPermissions' as const;
/** Singular variant seen in some draft references; tried as a fallback. */
export const WALLET_REVOKE_METHOD_LEGACY = 'wallet_revokeExecutionPermission' as const;

interface ProviderRequestShape {
  request: (args: { method: string; params?: unknown[] }) => Promise<unknown>;
}

export type RevokeOutcome = 'revoked' | 'cancelled' | 'unsupported';

/**
 * Revoke a single grant by its `permissionContext`, trying the canonical
 * method name then the singular legacy variant. `-32601` / `4200` on
 * BOTH ⇒ the wallet has no programmatic revoke ⇒ `unsupported`. `4001`
 * ⇒ the user dismissed the prompt ⇒ `cancelled`. Exported for unit tests.
 */
export async function revokeContextRpc(
  provider: ProviderRequestShape,
  permissionContext: Hex,
): Promise<RevokeOutcome> {
  const params = [{ permissionContext }];
  try {
    await provider.request({ method: WALLET_REVOKE_METHOD, params });
    return 'revoked';
  } catch (err: unknown) {
    if (isProviderError(err) && err.code === 4001) return 'cancelled';
    if (isProviderError(err) && (err.code === -32601 || err.code === 4200)) {
      try {
        await provider.request({ method: WALLET_REVOKE_METHOD_LEGACY, params });
        return 'revoked';
      } catch (err2: unknown) {
        if (isProviderError(err2) && err2.code === 4001) return 'cancelled';
        if (isProviderError(err2) && (err2.code === -32601 || err2.code === 4200)) {
          return 'unsupported';
        }
        throw err2;
      }
    }
    throw err;
  }
}

/**
 * Best-effort, prompt-free check for whether the wallet exposes the
 * ERC-7715 revoke method at all. Called with empty params: an
 * unsupported wallet rejects with method-not-found (-32601 / 4200) at
 * the middleware layer; a supported one rejects with invalid-params (or
 * similar) — both WITHOUT showing a prompt. Lets the UI hide a Clear
 * button that would otherwise lie about being able to revoke. Exported
 * for tests.
 */
export async function probeRevokeRpc(provider: ProviderRequestShape): Promise<boolean> {
  for (const method of [WALLET_REVOKE_METHOD, WALLET_REVOKE_METHOD_LEGACY]) {
    try {
      await provider.request({ method, params: [] });
      return true;
    } catch (err: unknown) {
      if (isProviderError(err) && (err.code === -32601 || err.code === 4200)) {
        continue;
      }
      // Any other rejection means the method IS registered (the wallet
      // got far enough to validate params) ⇒ revoke is supported.
      return true;
    }
  }
  return false;
}

export interface RevokeResult {
  revoked: Hex[];
  failed: Hex[];
  /** True when the wallet doesn't support programmatic 7715 revoke at all. */
  unsupported: boolean;
}

export function useRevokeProtectionGrants(): {
  revokeContexts: (contexts: Hex[]) => Promise<RevokeResult>;
  probeRevokeSupport: () => Promise<boolean>;
  isRevoking: boolean;
} {
  const { address } = useWallet();
  const { wallets } = useWallets();
  const [isRevoking, setIsRevoking] = useState(false);

  const revokeContexts = async (contexts: Hex[]): Promise<RevokeResult> => {
    const result: RevokeResult = { revoked: [], failed: [], unsupported: false };
    const unique = [...new Set(contexts)];
    if (unique.length === 0 || !address) return result;
    const active = wallets.find(
      (w) => w.address?.toLowerCase() === address.toLowerCase(),
    );
    if (!active) {
      result.failed.push(...unique);
      return result;
    }

    setIsRevoking(true);
    try {
      const provider = (await active.getEthereumProvider()) as ProviderRequestShape;
      for (const ctx of unique) {
        // Once the wallet has told us it doesn't support revoke, stop
        // prompting — the rest are unreachable.
        if (result.unsupported) {
          result.failed.push(ctx);
          continue;
        }
        let outcome: RevokeOutcome;
        try {
          outcome = await revokeContextRpc(provider, ctx);
        } catch {
          result.failed.push(ctx);
          continue;
        }
        if (outcome === 'revoked') {
          result.revoked.push(ctx);
          continue;
        }
        if (outcome === 'unsupported') {
          result.unsupported = true;
          result.failed.push(ctx);
          continue;
        }
        // 'cancelled' — the user dismissed the prompt; don't pester them
        // with the remaining grants.
        result.failed.push(ctx);
        break;
      }
    } catch {
      // Provider acquisition failed — record everything not already done
      // as failed, swallow (best-effort).
      for (const ctx of unique) {
        if (!result.revoked.includes(ctx) && !result.failed.includes(ctx)) {
          result.failed.push(ctx);
        }
      }
    } finally {
      setIsRevoking(false);
    }
    return result;
  };

  const probeRevokeSupport = useCallback(async (): Promise<boolean> => {
    if (!address) return false;
    const active = wallets.find(
      (w) => w.address?.toLowerCase() === address.toLowerCase(),
    );
    if (!active) return false;
    try {
      const provider = (await active.getEthereumProvider()) as ProviderRequestShape;
      return await probeRevokeRpc(provider);
    } catch {
      return false;
    }
  }, [wallets, address]);

  return { revokeContexts, probeRevokeSupport, isRevoking };
}

export interface RevokableGrant {
  delegationId: number;
  permissionContext: Hex;
}

/**
 * Of the rows represented in `grants`, return the delegationIds whose
 * EVERY permissionContext was successfully revoked. A row with a
 * partially-failed revoke is left out (it still has a live context, so
 * we keep counting it).
 */
export function pickFullyRevokedIds(grants: RevokableGrant[], revoked: Hex[]): number[] {
  const revokedSet = new Set(revoked);
  const byId = new Map<number, Hex[]>();
  for (const g of grants) {
    const arr = byId.get(g.delegationId);
    if (arr) arr.push(g.permissionContext);
    else byId.set(g.delegationId, [g.permissionContext]);
  }
  const ids: number[] = [];
  for (const [id, ctxs] of byId) {
    if (ctxs.length > 0 && ctxs.every((c) => revokedSet.has(c))) ids.push(id);
  }
  return ids;
}

/**
 * Best-effort: tell the server these delegation rows are now revoked
 * onchain (POST /api/protection/grants) so they drop out of the
 * holder's superseded count. Swallows errors — cleanup accuracy must
 * never break the flow it's bolted onto.
 */
export async function markGrantsClearedOnServer(
  holder: string,
  delegationIds: number[],
): Promise<void> {
  if (delegationIds.length === 0) return;
  try {
    await fetch('/api/protection/grants', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ holder, delegationIds }),
    });
  } catch {
    /* best-effort */
  }
}
